import { useState, useEffect, FormEvent } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  User,
  Shield,
  Building2,
  MapPin,
  Briefcase,
  Lock,
  Mail,
  Phone,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
} from "lucide-react";

interface StaffProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  profilePicture?: string;
  status: string;
  role?: { id: string; name: string };
  branch?: { id: string; name: string };
  department?: { id: string; name: string };
  jobTitle?: { id: string; name: string };
  tenant?: { name: string; slug: string };
  createdAt: string;
}

export function StaffProfilePage() {
  const { user } = useAuth();
  const { success, error } = useToast();
  const isStaff = user?.isStaff === true;
  const profileBase = isStaff ? "/staff" : "/users";

  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [phone, setPhone] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  // Password form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once auth user type is known
  }, [profileBase]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${profileBase}/me`);
      setProfile(res.data);
      setPhone(res.data.phone || "");
    } catch {
      setProfile(null);
      error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      const res = await api.patch(`${profileBase}/me/profile`, { phone });
      setProfile(res.data);
      success("Profile updated successfully");
    } catch {
      error("Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      error("Password must be at least 8 characters");
      return;
    }

    setPasswordSaving(true);
    try {
      await api.post(`${profileBase}/me/change-password`, {
        currentPassword,
        newPassword,
      });
      success("Password changed successfully");
      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to change password";
      error(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>Could not load your profile.</p>
      </div>
    );
  }

  const initials =
    `${(profile.firstName || "")[0] || ""}${(profile.lastName || "")[0] || ""}`.toUpperCase();

  return (
    <PageContainer>
      <PageHeader
        title="My Profile"
        description={
          isStaff ? "View and manage your staff account" : "View and manage your account"
        }
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "My Profile" }]}
        showSearch={false}
        titleSize="md"
      />

      {/* Profile Overview */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold flex-shrink-0">
              {profile.profilePicture ? (
                <img
                  src={profile.profilePicture}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                initials || <User className="w-10 h-10" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-900">
                {profile.firstName} {profile.lastName}
              </h2>
              <p className="text-gray-600">{profile.email}</p>

              <div className="flex flex-wrap gap-4 mt-3">
                {profile.role && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                    <Shield className="w-4 h-4 text-primary" />
                    {profile.role.name}
                  </div>
                )}
                {profile.branch && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                    <MapPin className="w-4 h-4 text-green-600" />
                    {profile.branch.name}
                  </div>
                )}
                {profile.department && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    {profile.department.name}
                  </div>
                )}
                {profile.jobTitle && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                    <Briefcase className="w-4 h-4 text-orange-600" />
                    {profile.jobTitle.name}
                  </div>
                )}
              </div>
            </div>

            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                profile.status === "ACTIVE"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {profile.status}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-gray-500" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm text-gray-600">Email</Label>
            <Input value={profile.email} disabled className="bg-gray-50 mt-1" />
            <p className="text-xs text-gray-500 mt-1">
              Contact your administrator to change your email address.
            </p>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Phone</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleProfileSave}
              disabled={profileSaving || phone === (profile.phone || "")}
            >
              {profileSaving ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            Account Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Organization</Label>
              <p className="font-medium text-gray-900 mt-1">
                {profile.tenant?.name || user?.tenantId || "—"}
              </p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Role</Label>
              <p className="font-medium text-gray-900 mt-1">{profile.role?.name || "—"}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Branch</Label>
              <p className="font-medium text-gray-900 mt-1">{profile.branch?.name || "—"}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Department</Label>
              <p className="font-medium text-gray-900 mt-1">{profile.department?.name || "—"}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Job Title</Label>
              <p className="font-medium text-gray-900 mt-1">{profile.jobTitle?.name || "—"}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Member Since</Label>
              <p className="font-medium text-gray-900 mt-1">
                {new Date(profile.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            These details are managed by your administrator. Contact them for changes.
          </p>
        </CardContent>
      </Card>

      {/* Security - Password Change */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-500" />
              Security
            </CardTitle>
            {!showPasswordForm && (
              <Button variant="outline" onClick={() => setShowPasswordForm(true)}>
                Change Password
              </Button>
            )}
          </div>
        </CardHeader>
        {showPasswordForm && (
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <Label required>Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPw ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label required>New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>
              <div>
                <Label required>Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1"
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={
                    passwordSaving ||
                    !currentPassword ||
                    !newPassword ||
                    newPassword !== confirmPassword
                  }
                >
                  {passwordSaving ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Change Password
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>
    </PageContainer>
  );
}
